import { api } from '~/shared/api/client'
import type {
  AffiliateCodeLookup,
  AuthResult,
  AuthTokens,
  LoginInput,
  OtpVerifyInput,
  PasswordChangeInput,
  PasswordResetConfirmInput,
  PasswordSetInput,
  RegisterInput,
  SessionPayload,
  SignupCodeSent,
} from '../types'

/**
 * Auth endpoints.
 *
 * Every call goes through `shared/api/client`, so responses arrive unwrapped and failures
 * arrive as `ApmpApiError` with a `code` the caller branches on.
 *
 * No refresh token is ever passed or read here: it lives in the `HttpOnly` cookie the browser
 * attaches on its own (`withCredentials` is set on the axios instance).
 */
export const authApi = {
  login: (input: LoginInput) => api.post<AuthResult>('/auth/login/', input),

  requestOtp: (identifier: string) => api.post<AuthResult>('/auth/otp/request/', { identifier }),

  verifyOtp: (input: OtpVerifyInput) => api.post<AuthResult>('/auth/otp/verify/', input),

  refresh: () =>
    api.post<AuthResult>('/auth/token/refresh/', {}).then((result) => {
      if (!result.tokens) throw new Error('refresh returned no tokens')
      return result.tokens
    }),

  logout: () => api.post<void>('/auth/logout/', {}),

  /**
   * `silentAuthFailure`: an anonymous visitor's 401 here is the expected answer, not an
   * expired session. Without it, every first page load pushed the visitor to the login page
   * carrying a `next` that pointed at wherever the router had not yet navigated.
   */
  session: () => api.get<SessionPayload>('/auth/session/', { silentAuthFailure: true }),

  setPassword: (input: PasswordSetInput) =>
    api.post<AuthResult>('/auth/password/set/', input).then((r) => r.tokens as AuthTokens),

  changePassword: (input: PasswordChangeInput) =>
    api.post<AuthResult>('/auth/password/change/', input).then((r) => r.tokens as AuthTokens),

  // ---- Forgotten password (`/auth/forgot-password`) -------------------------------------
  /** Same answer whether or not the account exists; the server never says. */
  requestPasswordReset: (identifier: string) =>
    api.post<AuthResult>('/auth/password/reset/request/', { identifier }),
  /** Spends the reset code on a new password and returns a signed-in token pair. */
  confirmPasswordReset: (input: PasswordResetConfirmInput) =>
    api.post<AuthResult>('/auth/password/reset/confirm/', input),
  /**
   * The welcome link (`/auth/welcome`): spends the welcome's one-time code on a first password
   * and returns a signed-in token pair. Same body as the reset confirm.
   */
  setupPassword: (input: PasswordResetConfirmInput) =>
    api.post<AuthResult>('/auth/password/setup/', input),

  // ---- Phase 06 -----------------------------------------------------------------------
  /** Since 2026-10-27 (D26): sends the code; the account is made by `contactsApi.registerConfirm`. */
  register: (input: RegisterInput) => api.post<SignupCodeSent>('/auth/register/', input),
  /** Resolve a code to an officer's display name for the signup page. Never blocks signup. */
  lookupAffiliateCode: (code: string) =>
    api.get<AffiliateCodeLookup>(`/auth/affiliate-code/?code=${encodeURIComponent(code)}`),
}
